import React, { useState, useEffect } from 'react';
import { useTheme, getT, getBrandColors} from '../ThemeContext';
import PageHeaderIcon from "../components/PageHeaderIcon";
import { PrimaryButton } from "../components/ThemedButton";
import API_BASE from '../apiConfig';
import { getSessionUser } from '../utils/session';
import { FaMoneyBillWave, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { useSort } from '../hooks/useSort';

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

const EXPENSE_TYPES = [
  { value: 'TOURNAMENT_COST', label: 'Tournament' },
  { value: 'COACH_COST', label: 'Coach' },
  { value: 'UMPIRE_COST_1', label: 'Umpire 1' },
  { value: 'UMPIRE_COST_2', label: 'Umpire 2' },
  { value: 'BALL_COST', label: 'Ball' },
  { value: 'MEDAL_COST', label: 'Medal' },
  { value: 'MISCELLANEOUS', label: 'Misc' },
];
const TYPE_LABEL = Object.fromEntries(EXPENSE_TYPES.map(t => [t.value, t.label]));

const TournamentExpense = ({ embedded = false, extTournamentId = '', extAgeCategoryId = '' } = {}) => {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const user = getSessionUser();

  const [tournaments, setTournaments] = useState([]);
  const [tournamentId, setTournamentId] = useState('');
  const [tournament, setTournament] = useState(null);
  const [ageCategoryId, setAgeCategoryId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expenses, setExpenses] = useState([]);

  const [datePaid, setDatePaid] = useState('');
  const [notes, setNotes] = useState('');
  const [amounts, setAmounts] = useState({}); // { catId: { type: value } }

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadTournaments(); }, []);

  const loadTournaments = async () => {
    try {
      const res = await fetch(`${BASE}/tournament`);
      const data = await res.json();
      if (data.success) setTournaments(data.tournaments || []);
    } catch (err) { console.error(err); }
  };

  const onTournamentChange = async (id) => {
    setTournamentId(id);
    setTournament(null);
    setAgeCategoryId(''); setDateFrom(''); setDateTo('');
    setAmounts({}); setError(''); setSuccess('');
    if (!id) { setExpenses([]); return; }
    try {
      const res = await fetch(`${BASE}/tournament/${id}`);
      const data = await res.json();
      if (data.success) setTournament(data);
      await loadExpenses(id, '');
    } catch (err) { setError(err.message); }
  };

  const loadExpenses = async (id, catId) => {
    try {
      const q = catId ? `?age_category_id=${catId}` : '';
      const res = await fetch(`${BASE}/tournament/${id}/expenses${q}`);
      const data = await res.json();
      if (data.success) setExpenses(data.expenses || []);
    } catch (err) { console.error(err); }
  };

  const applyFilters = () => loadExpenses(tournamentId, ageCategoryId);

  // Embedded (hub) mode: one coherent load (detail + expenses) on mount and on
  // any shared-filter change — avoids the two-effect race.
  useEffect(() => {
    if (!embedded) return;
    if (!extTournamentId) { setTournament(null); setExpenses([]); setTournamentId(''); return; }
    setTournamentId(String(extTournamentId));
    setAgeCategoryId(extAgeCategoryId ? String(extAgeCategoryId) : '');
    (async () => {
      try { const res = await fetch(`${BASE}/tournament/${extTournamentId}`); const dt = await res.json(); if (dt.success) setTournament(dt); } catch (e) { /* ignore */ }
      loadExpenses(extTournamentId, extAgeCategoryId || '');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, extTournamentId, extAgeCategoryId]);

  // Booked list is filtered by the date range client-side (category is applied server-side).
  const shownExpenses = expenses.filter(e =>
    (!dateFrom || (e.date_paid && e.date_paid >= dateFrom)) &&
    (!dateTo || (e.date_paid && e.date_paid <= dateTo)));

  // Standard rule: every table column is sortable — click to sort, click again to flip.
  const bookedSort = useSort(shownExpenses, "date_paid", "desc");

  const setCell = (catId, type, val) => {
    setAmounts(prev => ({ ...prev, [catId]: { ...(prev[catId] || {}), [type]: val } }));
  };

  const rowTotal = (catId) => EXPENSE_TYPES.reduce((sum, t) => sum + (parseFloat(amounts[catId]?.[t.value]) || 0), 0);
  const grandTotal = tournament ? (tournament.age_categories || []).reduce((s, c) => s + rowTotal(c.age_category_id), 0) : 0;

  const bookAll = async () => {
    if (!datePaid) { setError('Select the date paid'); return; }
    const items = [];
    (tournament.age_categories || []).forEach(c => {
      EXPENSE_TYPES.forEach(t => {
        const amt = parseFloat(amounts[c.age_category_id]?.[t.value]);
        if (amt > 0) items.push({ age_category_id: c.age_category_id, expense_type: t.value, amount: amt, quantity: 1 });
      });
    });
    if (items.length === 0) { setError('Enter at least one expense amount'); return; }

    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE}/tournament/${tournamentId}/expenses/bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, date_paid: datePaid, notes, items }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(data.message);
        setAmounts({}); setNotes('');
        loadExpenses(tournamentId);
      } else {
        setError(data.message || data.detail || 'Error booking expenses');
      }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const glassCard = { background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, border: T.glassBdr, borderRadius: '14px', boxShadow: T.glassSh, padding: '18px 22px', boxSizing: 'border-box', marginBottom: '14px' };
  const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textLabel, fontFamily: FONT };
  const inputStyle = { border: `1.5px solid ${T.inputBdr}`, borderRadius: '8px', padding: '8px 12px', background: T.inputBg, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, width: '100%', boxSizing: 'border-box', outline: 'none', fontSize: '13px', color: T.inputText, fontFamily: FONT };
  const cellInput = { width: '100%', padding: '7px 8px', border: `1.5px solid ${T.inputBdr}`, borderRadius: '6px', background: T.inputBg, color: T.inputText, fontSize: '12.5px', outline: 'none', fontFamily: FONT, textAlign: 'right', boxSizing: 'border-box' };
  const th = { padding: '9px 8px', fontWeight: 800, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: FONT, color: T.textLabel, whiteSpace: 'nowrap' };
  const td = { padding: '6px 8px', fontSize: '12.5px', fontFamily: FONT, color: T.textPrimary, verticalAlign: 'middle' };

  return (
    <div style={embedded ? { padding: 0, boxSizing: 'border-box' } : { minHeight: '100dvh', padding: '18px 26px 40px', boxSizing: 'border-box' }}>
      {!embedded && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <PageHeaderIcon icon={<FaMoneyBillWave size={19} color={getBrandColors().blueDark} />} />
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 800, color: T.textPrimary, margin: 0, fontFamily: FONT }}>Tournament Expense</h1>
          <p style={{ fontSize: '12px', color: T.textMuted, margin: '2px 0 0', fontFamily: FONT }}>Book actual expenses for every category in one go</p>
        </div>
      </div>
      )}

      {error && (
        <div style={{ display: 'flex', gap: '10px', padding: '10px 14px', borderRadius: '9px', marginBottom: '14px', fontSize: '12.5px', fontWeight: 600, fontFamily: FONT, background: 'rgba(248,113,113,0.12)', color: getBrandColors().red, border: '1px solid rgba(248,113,113,0.30)' }}>
          <FaExclamationCircle size={14} /><span>{error}</span>
        </div>
      )}
      {success && (
        <div style={{ display: 'flex', gap: '10px', padding: '10px 14px', borderRadius: '9px', marginBottom: '14px', fontSize: '12.5px', fontWeight: 600, fontFamily: FONT, background: 'rgba(52,211,153,0.12)', color: getBrandColors().green, border: '1px solid rgba(52,211,153,0.30)' }}>
          <FaCheckCircle size={14} /><span>{success}</span>
        </div>
      )}

      {!embedded && (
      <div style={glassCard}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '14px' }}>
          <div>
            <label style={labelStyle}>Tournament</label>
            <select value={tournamentId} onChange={(e) => onTournamentChange(e.target.value)} style={inputStyle}>
              <option value="">— Choose —</option>
              {tournaments.map(t => (
                <option key={t.tournament_id} value={t.tournament_id}>{t.tournament_name} ({t.status})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Age Category</label>
            <select value={ageCategoryId} onChange={(e) => setAgeCategoryId(e.target.value)} style={inputStyle} disabled={!tournament}>
              <option value="">All</option>
              {tournament?.age_categories.map(c => (
                <option key={c.age_category_id} value={c.age_category_id}>{c.category_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputStyle} />
          </div>
        </div>
        <button onClick={applyFilters} disabled={!tournamentId} style={{
          marginTop: '14px', padding: '9px 18px', borderRadius: '8px',
          background: T.glass, border: T.glassBdr, color: T.inputText, fontWeight: 700,
          fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
        }}>
          Apply Filters
        </button>
      </div>
      )}

      {tournament && (
        <div style={glassCard}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '14px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Date Paid *</label>
              <input type="date" value={datePaid} onChange={(e) => setDatePaid(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Notes (optional, applies to all)</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g., paid via bank transfer" style={inputStyle} />
            </div>
          </div>

          <label style={{ ...labelStyle, marginBottom: '10px' }}>Enter amounts — one row per category, one column per expense type</label>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.inputBdr}` }}>
                  <th style={{ ...th, textAlign: 'left' }}>Category</th>
                  {EXPENSE_TYPES.map(t => <th key={t.value} style={{ ...th, textAlign: 'right' }}>{t.label}</th>)}
                  <th style={{ ...th, textAlign: 'right' }}>Row Total</th>
                </tr>
              </thead>
              <tbody>
                {(tournament.age_categories || []).map((c, idx) => (
                  <tr key={c.age_category_id} style={{ background: idx % 2 === 0 ? 'transparent' : T.glass, borderBottom: `1px solid ${T.inputBdr}` }}>
                    <td style={{ ...td, fontWeight: 700 }}>{c.category_name}</td>
                    {EXPENSE_TYPES.map(t => (
                      <td key={t.value} style={{ ...td, minWidth: '90px' }}>
                        <input type="number" step="0.01" min="0" placeholder="0"
                          value={amounts[c.age_category_id]?.[t.value] || ''}
                          onChange={(e) => setCell(c.age_category_id, t.value, e.target.value)}
                          style={cellInput} />
                      </td>
                    ))}
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>${rowTotal(c.age_category_id).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
            <div style={{ fontSize: '13px', color: T.textMuted, fontFamily: FONT }}>
              Grand total to book: <strong style={{ color: T.textPrimary, fontSize: '16px' }}>${grandTotal.toFixed(2)}</strong>
            </div>
            <PrimaryButton onClick={bookAll} disabled={loading}>
              {loading ? 'Booking...' : 'Book Expenses'}
            </PrimaryButton>
          </div>
        </div>
      )}

      {tournament && shownExpenses.length > 0 && (
        <div style={glassCard}>
          <h2 style={{ fontSize: '15px', fontWeight: 700, color: T.textPrimary, margin: '0 0 14px', fontFamily: FONT }}>Booked Expenses</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: FONT }}>
              <thead>
                <tr style={{ background: T.glass, borderBottom: `2px solid ${T.inputBdr}` }}>
                  <th style={{ ...th, textAlign: 'left', ...bookedSort.thSort('category_name', T) }} onClick={() => bookedSort.toggleSort('category_name')}>Category{bookedSort.sortArrow('category_name')}</th>
                  <th style={{ ...th, textAlign: 'left', ...bookedSort.thSort('expense_type', T) }} onClick={() => bookedSort.toggleSort('expense_type')}>Type{bookedSort.sortArrow('expense_type')}</th>
                  <th style={{ ...th, textAlign: 'right', ...bookedSort.thSort('amount', T) }} onClick={() => bookedSort.toggleSort('amount')}>Amount{bookedSort.sortArrow('amount')}</th>
                  <th style={{ ...th, textAlign: 'center', ...bookedSort.thSort('quantity', T) }} onClick={() => bookedSort.toggleSort('quantity')}>Qty{bookedSort.sortArrow('quantity')}</th>
                  <th style={{ ...th, textAlign: 'center', ...bookedSort.thSort('date_paid', T) }} onClick={() => bookedSort.toggleSort('date_paid')}>Date Paid{bookedSort.sortArrow('date_paid')}</th>
                  <th style={{ ...th, textAlign: 'left', ...bookedSort.thSort('notes', T) }} onClick={() => bookedSort.toggleSort('notes')}>Notes{bookedSort.sortArrow('notes')}</th>
                </tr>
              </thead>
              <tbody>
                {bookedSort.sorted.map((e, idx) => (
                  <tr key={e.expense_id} style={{ background: idx % 2 === 0 ? 'transparent' : T.glass, borderBottom: `1px solid ${T.inputBdr}`, color: T.textPrimary }}>
                    <td style={td}>{e.category_name}</td>
                    <td style={td}>{TYPE_LABEL[e.expense_type] || e.expense_type}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>${Number(e.amount).toFixed(2)}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{e.quantity}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{e.date_paid}</td>
                    <td style={{ ...td, color: T.textMuted }}>{e.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentExpense;
